function formatRelativeSyncAge(value, now = Date.now()) {
  const dateMs = new Date(value).getTime();

  if (!Number.isFinite(dateMs)) {
    return '—';
  }

  const diffMs = Math.max(0, now - dateMs);
  const minutes = Math.floor(diffMs / (60 * 1000));

  if (minutes < 1) {
    return 'Just now';
  }

  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);

  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  const weeks = Math.floor(days / 7);

  if (weeks < 5) {
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }

  const months = Math.floor(days / 30);
  return `${Math.max(months, 1)} month${months === 1 ? '' : 's'} ago`;
}

function formatSyncCalendarDate(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).formatToParts(date);

  const day = parts.find((part) => part.type === 'day')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const year = parts.find((part) => part.type === 'year')?.value;

  return `${day}/${month}/${year}`;
}

function statusLabel(status) {
  if (status === 'succeeded') return 'Succeeded';
  if (status === 'failed') return 'Failed';
  if (status === 'running') return 'Running';
  if (status === 'queued') return 'Queued';
  return status || 'Unknown';
}

/**
 * Shows last Amazon sync run time, status, and summary for ops recovery.
 * Pass compact to show only last sync time + status (order-date style).
 */
export function SyncRunBanner({ job, loading, compact = false }) {
  if (loading && !job) {
    return (
      <p className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-4 py-2.5 text-sm text-[rgb(var(--crm-text-muted))]">
        Checking last sync…
      </p>
    );
  }

  if (!job) {
    return (
      <p className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-4 py-2.5 text-sm text-[rgb(var(--crm-text-muted))]">
        No Amazon sync run yet. Auto-sync runs every 15 minutes, or use{' '}
        <span className="font-medium text-[rgb(var(--crm-text))]">Sync now</span> to pull immediately.
      </p>
    );
  }

  const syncAt = job.finishedAt || job.startedAt || job.createdAt;
  const tone =
    job.status === 'failed'
      ? 'border-red-200/80 bg-red-50 text-red-800'
      : job.status === 'running' || job.status === 'queued'
        ? 'border-amber-200/80 bg-amber-50 text-amber-900'
        : 'border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] text-[rgb(var(--crm-text))]';

  const statusDot =
    job.status === 'failed'
      ? 'bg-red-500'
      : job.status === 'running' || job.status === 'queued'
        ? 'bg-amber-500'
        : 'bg-emerald-500';

  if (compact) {
    const inProgress = job.status === 'running' || job.status === 'queued';
    return (
      <div className={`inline-flex items-start gap-3 rounded-xl border px-4 py-3 ${tone}`}>
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${statusDot}`} aria-hidden="true" />
        <div className="grid gap-0.5 leading-snug">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
            {inProgress ? 'Sync status' : 'Last sync'}
          </span>
          <strong className="text-sm font-semibold text-[rgb(var(--crm-text))]">
            {inProgress
              ? job.status === 'queued'
                ? 'Waiting to start…'
                : 'Syncing from Amazon…'
              : syncAt
                ? formatRelativeSyncAge(syncAt)
                : '—'}
          </strong>
          {!inProgress && syncAt ? (
            <span className="text-sm text-[rgb(var(--crm-text))]">
              {formatSyncCalendarDate(syncAt)}
            </span>
          ) : null}
          <span className="text-xs text-[rgb(var(--crm-text-muted))]">
            {inProgress
              ? 'You can leave this page — sync continues in the background.'
              : statusLabel(job.status)}
          </span>
        </div>
      </div>
    );
  }

  const when = syncAt ? formatRelativeSyncAge(syncAt) : null;
  const synced = job.result?.synced;
  const countBit =
    typeof synced === 'number' ? ` · ${synced} saved` : '';

  const restock = job.result?.autoRestock;
  const restockBit =
    restock && typeof restock === 'object'
      ? (() => {
          const updated = Number(restock.updated || 0);
          const failed = Number(restock.failed || 0);
          const checked = Number(restock.checked || 0);
          if (!checked && !updated && !failed) return '';
          const bits = [`${updated} auto-restocked`];
          if (failed) bits.push(`${failed} failed`);
          return ` · ${bits.join(', ')}`;
        })()
      : '';

  return (
    <p className={`rounded-xl border px-4 py-2.5 text-sm ${tone}`}>
      <span className="font-medium">Last sync:</span> {when || '—'}
      {syncAt ? ` · ${formatSyncCalendarDate(syncAt)}` : ''}
      {' · '}
      {statusLabel(job.status)}
      {countBit}
      {restockBit}
      {job.message ? ` — ${job.message}` : null}
    </p>
  );
}
